import io from "socket.io-client";
import api from "./api";

const MAX_SERVER_DISCONNECT_RETRIES = 5;

// Lido a cada (re)conexão: o token muda após login/refresh e não pode ficar congelado no construtor
const getSessionToken = () => {
  try {
    const rawToken = localStorage.getItem("token");
    return rawToken ? `Bearer ${JSON.parse(rawToken)}` : null;
  } catch (err) {
    return null;
  }
};

class SocketWorker {
  constructor(companyId , userId) {
    if (!SocketWorker.instance) {
      this.companyId = companyId
      this.userId = userId
      this.socket = null;
      this.serverDisconnectRetries = 0;
      this.serverDisconnectTimer = null;
      this.sessionReplaced = false;
      this.authToken = null;
      this.configureSocket();
      this.eventListeners = {};
      this.connectionCallbacks = {};
      SocketWorker.instance = this;

    } 

    return SocketWorker.instance;
  }

  configureSocket() {
    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}` , {
      // Sem token o backend derruba a conexão; connect() abre quando o token existir
      autoConnect: Boolean(getSessionToken()),
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
      // transports: ["websocket", "polling", "flashsocket"],
      // pingTimeout: 18000,
      // pingInterval: 18000,
      query: { userId: this.userId },
      auth: (cb) => {
        this.authToken = getSessionToken();
        cb({ token: this.authToken });
      }
    });

    this.socket.on("connect", () => {
      console.log(
        `[Socket.IO] Conectado | id=${this.socket.id} | transport=${this.socket.io.engine?.transport?.name}`
      );
      this.serverDisconnectRetries = 0;
      this._notifyConnectionCallbacks("connected");
    });

    this.socket.on("disconnect", (reason, details) => {
      // Motivos possíveis do socket.io v4:
      // "io server disconnect" -> servidor derrubou a conexão manualmente (ex: token inválido/expirado)
      // "io client disconnect" -> disconnect() chamado no próprio client
      // "ping timeout"         -> servidor não respondeu ao ping a tempo (rede instável/servidor sobrecarregado)
      // "transport close"      -> conexão de rede caiu (troca de rede, aba em background, etc)
      // "transport error"      -> erro no transporte (ex: websocket handshake falhou)
      console.warn(
        `[Socket.IO] Desconectado | motivo="${reason}" | id=${this.socket.id}`,
        details || ""
      );
      this._notifyConnectionCallbacks("disconnected", reason);

      // Nesse caso o socket.io não reconecta sozinho
      if (reason === "io server disconnect" && !this.sessionReplaced) {
        this._reconnectWithFreshToken();
      }
    });

    // Usuário logou em outro lugar: o servidor derruba este socket logo em seguida
    this.socket.on("session:replaced", () => {
      console.warn("[Socket.IO] Sessão substituída por um novo login");
      this.sessionReplaced = true;
      this._notifyConnectionCallbacks("session_replaced", this.authToken);
    });

    this.socket.on("connect_error", (error) => {
      console.error(
        `[Socket.IO] Erro de conexão | message="${error.message}" | type=${error.type ?? "n/a"} | description=${JSON.stringify(error.description ?? null)}`,
        error
      );
      this._notifyConnectionCallbacks("error", error);
    });

    this.socket.on("reconnect_attempt", (attemptNumber) => {
      console.log(
        `[Socket.IO] Tentativa de reconexão #${attemptNumber} | transport=${this.socket.io.engine?.transport?.name ?? "n/a"}`
      );
      this._notifyConnectionCallbacks("reconnecting", attemptNumber);
    });

    this.socket.on("reconnect", (attemptNumber) => {
      console.log(`[Socket.IO] Reconectado após ${attemptNumber} tentativa(s) | id=${this.socket.id}`);
      this._notifyConnectionCallbacks("connected");
    });

    this.socket.on("reconnect_error", (error) => {
      console.error(
        `[Socket.IO] Erro durante reconexão | message="${error.message}" | type=${error.type ?? "n/a"} | description=${JSON.stringify(error.description ?? null)}`,
        error
      );
      this._notifyConnectionCallbacks("reconnect_error", error);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("[Socket.IO] Todas as tentativas de reconexão falharam");
      this._notifyConnectionCallbacks("reconnect_failed");
    });

    this.socket.io.engine?.on?.("close", (reason) => {
      console.warn(`[Socket.IO] Engine fechou a conexão | motivo="${reason}"`);
    });
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {

    this.connect();
    this.socket.on(event, callback);

    // Armazena o ouvinte no objeto de ouvintes
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    // console.log(event, callback)
    this.connect();
    if (this.eventListeners[event]) {
      // console.log("Desconectando do servidor Socket.IO:", event, callback);
      if (callback) {
        // Desconecta um ouvinte específico
        this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        // console.log("DELETOU EVENTOS DO SOCKET:", this.eventListeners[event]);

        // Desconecta todos os ouvintes do evento
        this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
      // console.log("EVENTOS DO SOCKET:", this.eventListeners);
    }
  }

  disconnect() {
    clearTimeout(this.serverDisconnectTimer);
    this.serverDisconnectTimer = null;
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null
      SocketWorker.instance = null
      console.log("Socket desconectado manualmente");
    }
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      if (!this.socket || !this.socket.connected) {
        console.log("Tentando reconectar após desconexão");
        this.connect();
      }
    }, 1000);
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.active && !this.serverDisconnectTimer && !this.sessionReplaced && getSessionToken()) {
      this.socket.connect();
    }
  }

  _reconnectWithFreshToken() {
    if (this.serverDisconnectTimer) return;
    if (this.serverDisconnectRetries >= MAX_SERVER_DISCONNECT_RETRIES) {
      console.error("[Socket.IO] Servidor recusou a conexão repetidamente; desistindo até nova ação do usuário");
      this._notifyConnectionCallbacks("reconnect_failed");
      return;
    }

    const delay = Math.min(1000 * 2 ** this.serverDisconnectRetries, 30000);
    this.serverDisconnectRetries += 1;

    this.serverDisconnectTimer = setTimeout(async () => {
      this.serverDisconnectTimer = null;
      const socket = this.socket;
      if (!socket || socket.connected) return;

      try {
        const { data } = await api.post("/auth/refresh_token");
        if (data?.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
        }
      } catch (err) {
        console.warn("[Socket.IO] Falha ao renovar token antes de reconectar", err);
      }

      if (this.socket === socket && !socket.connected && getSessionToken()) {
        socket.connect();
      }
    }, delay);
  }

  // A sessão foi substituída por um login do mesmo usuário neste navegador:
  // reconecta com o token novo (o auth do handshake relê o localStorage)
  resumeSession() {
    this.sessionReplaced = false;
    this.serverDisconnectRetries = 0;
    if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
  }

  forceReconnect() {
    clearTimeout(this.serverDisconnectTimer);
    this.serverDisconnectTimer = null;
    this.serverDisconnectRetries = 0;
    if (this.socket) {
      this.socket.disconnect();
      this.socket.connect();
    }
  }

  onConnectionEvent(event, callback) {
    if (!this.connectionCallbacks[event]) {
      this.connectionCallbacks[event] = [];
    }
    this.connectionCallbacks[event].push(callback);

    if (event === "connected" && this.socket && this.socket.connected) {
      callback();
    }
  }

  offConnectionEvent(event, callback) {
    if (this.connectionCallbacks[event]) {
      if (callback) {
        this.connectionCallbacks[event] = this.connectionCallbacks[event].filter(
          (cb) => cb !== callback
        );
      } else {
        delete this.connectionCallbacks[event];
      }
    }
  }

  _notifyConnectionCallbacks(event, data) {
    if (this.connectionCallbacks[event]) {
      this.connectionCallbacks[event].forEach((cb) => cb(data));
    }
  }
}

// const instance = (companyId, userId) => new SocketWorker(companyId,userId);
const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;